/**
 * Tiled rendering utilities for displaying grid beyond boundaries
 * Requirements: 3.3, 3.4
 */

import type { SimulationState, SimulationConfig, Position } from '@langton/shared';
import { Topology } from '@langton/shared';

/**
 * Tile transformation for different topologies
 */
export interface TileTransform {
  /** Offset in grid units */
  offsetX: number;
  offsetY: number;
  /** Whether to mirror horizontally */
  mirrorX: boolean;
  /** Whether to mirror vertically */
  mirrorY: boolean;
}

/**
 * Calculate tile transformation for a given tile position and topology
 * Requirements: 3.3, 3.4
 * 
 * @param tileX - Tile X coordinate (0 = original, -1 = left, 1 = right)
 * @param tileY - Tile Y coordinate (0 = original, -1 = top, 1 = bottom)
 * @param topology - Grid topology
 * @returns Transformation to apply to the tile
 */
export function getTileTransform(
  tileX: number,
  tileY: number,
  topology: Topology
): TileTransform {
  const transform: TileTransform = {
    offsetX: tileX,
    offsetY: tileY,
    mirrorX: false,
    mirrorY: false
  };

  switch (topology) {
    case Topology.Torus:
      // Torus: simple repetition, no mirroring
      break;

    case Topology.KleinBottle:
      // Klein bottle: mirror vertically on horizontal wrap
      // Odd horizontal tiles are mirrored
      if (Math.abs(tileX) % 2 === 1) {
        transform.mirrorY = true;
      }
      break;

    case Topology.ProjectivePlane:
      // Projective plane: mirror on both axes
      // Odd horizontal tiles are mirrored vertically
      // Odd vertical tiles are mirrored horizontally
      if (Math.abs(tileX) % 2 === 1) {
        transform.mirrorY = true;
      }
      if (Math.abs(tileY) % 2 === 1) {
        transform.mirrorX = true;
      }
      break;

    case Topology.Sphere:
      // Sphere: mirror at poles (top and bottom)
      // This is a simplified representation
      if (Math.abs(tileY) % 2 === 1) {
        transform.mirrorX = true;
        transform.mirrorY = true;
      }
      break;
  }

  return transform;
}

/**
 * Check if a topology requires mirroring for tiled rendering
 */
export function isNonOrientableTopology(topology: Topology): boolean {
  return topology === Topology.KleinBottle || topology === Topology.ProjectivePlane;
}

/**
 * Get the cell value at a position, accounting for tiling and mirroring
 * Requirements: 3.3, 3.4
 */
export function getTiledCellValue(
  state: SimulationState,
  config: SimulationConfig,
  worldX: number,
  worldY: number
): number {
  const { width, height } = config;
  
  // Calculate which tile we're in
  const tileX = Math.floor(worldX / width);
  const tileY = Math.floor(worldY / height);
  
  // Get local position within the tile
  let localX = ((worldX % width) + width) % width;
  let localY = ((worldY % height) + height) % height;
  
  // Apply transformation based on topology
  const transform = getTileTransform(tileX, tileY, config.topology);
  
  if (transform.mirrorX) {
    localX = width - 1 - localX;
  }
  if (transform.mirrorY) {
    localY = height - 1 - localY;
  }
  
  // Return the cell value
  return state.grid[localY]?.[localX] ?? 0;
}

/**
 * Calculate visible tile range for rendering
 */
export function getVisibleTileRange(
  canvasWidth: number,
  canvasHeight: number,
  gridWidth: number,
  gridHeight: number,
  cellSize: number,
  offset: Position,
  zoom: number
): { minTileX: number; maxTileX: number; minTileY: number; maxTileY: number } {
  const effectiveCellSize = cellSize * zoom;
  const gridPixelWidth = gridWidth * effectiveCellSize;
  const gridPixelHeight = gridHeight * effectiveCellSize;
  
  // Calculate visible area in world coordinates
  const minWorldX = -offset.x;
  const maxWorldX = canvasWidth - offset.x;
  const minWorldY = -offset.y;
  const maxWorldY = canvasHeight - offset.y;
  
  // Convert to tile coordinates
  const minTileX = Math.floor(minWorldX / gridPixelWidth);
  const maxTileX = Math.ceil(maxWorldX / gridPixelWidth);
  const minTileY = Math.floor(minWorldY / gridPixelHeight);
  const maxTileY = Math.ceil(maxWorldY / gridPixelHeight);
  
  return { minTileX, maxTileX, minTileY, maxTileY };
}

/**
 * Apply canvas transformation for a tile
 */
export function applyTileTransform(
  ctx: CanvasRenderingContext2D,
  transform: TileTransform,
  tileWidth: number,
  tileHeight: number,
  tilePixelX: number,
  tilePixelY: number
): void {
  ctx.save();
  
  // Move to tile position
  ctx.translate(tilePixelX, tilePixelY);
  
  // Apply mirroring
  if (transform.mirrorX || transform.mirrorY) {
    // Move to center for mirroring
    ctx.translate(tileWidth / 2, tileHeight / 2);
    ctx.scale(transform.mirrorX ? -1 : 1, transform.mirrorY ? -1 : 1);
    ctx.translate(-tileWidth / 2, -tileHeight / 2);
  }
}

/**
 * Restore canvas state after tile rendering
 */
export function restoreTileTransform(ctx: CanvasRenderingContext2D): void {
  ctx.restore();
}
